/**
 * The calendar's toolbar pieces, the Practice calendar's composition. `CalendarPeriodControls` (the period named, with
 * the day picker behind it, then ‹ Today › grouped) and `CalendarViewSwitch` (Day / Week / Month / Room / Doctor as the
 * one segmented control) sit in `CalendarToolbarRow`, the row under the page header (Antonin, 15 Oct: "calendar
 * controls should go out of the header, one section below"): the period on the left, the practitioner filter centred
 * in the room between, the appointment search and the view switch at the far right (the Practice order).
 *
 * They replace the Signal DS `PACalendarSubToolbar`, whose date, steps and view switch were one component with the
 * filters (the filters moved into the floating pill, `calendar-filter-pill.tsx`). Same behaviour: the steps are the
 * Practice app's (a month on Month, a week on Week, a day elsewhere), the same Today jump, the same five views. The old trailing › after the
 * view switch was only "next", now one of the grouped steps.
 *
 * Look: the `.cal-toolbar` rules in the bridge, under its calendar toolbar heading. Graduation candidate: a
 * `PACalendarToolbar` row in Signal DS.
 */
import { useState, type ReactNode } from 'react';
import { DayPicker, type ClassNames } from 'react-day-picker';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  BrandSpinner,
  Segmented,
} from '@scripsteam/scrips-signal-ds';
import { periodLabel, showsToday, stepPeriod } from '@/scheduling/lib/calendar-period';
import { practiceNow } from '@/shared/lib/practice-time';
import type { DateFilter } from '@/types/scheduling-api';

const VIEW_OPTIONS: { label: string; value: DateFilter }[] = [
  { label: 'Day', value: 'Day' },
  { label: 'Week', value: 'Week' },
  { label: 'Month', value: 'Month' },
  { label: 'Room', value: 'Room' },
  { label: 'Doctor', value: 'Doctor' },
];

/**
 * The day picker's class names: plain hooks the bridge styles (`.cal-dp-*`, under "calendar toolbar"), so the picker
 * is the Practice calendar's: month and year dropdowns in the caption, a 36 px day grid, the selected day filled brand
 * blue, today a blue number in a hairline ring, and a footer with a blue Today link and the long date.
 */
const DAY_PICKER_CLASSNAMES: Partial<ClassNames> = {
  root: 'cal-dp',
  months: 'cal-dp-months',
  month: 'cal-dp-month',
  nav: 'cal-dp-nav',
  button_previous: 'cal-dp-nav-button',
  button_next: 'cal-dp-nav-button',
  month_caption: 'cal-dp-caption',
  dropdowns: 'cal-dp-dropdowns',
  dropdown_root: 'cal-dp-dropdown-root',
  dropdown: 'cal-dp-dropdown',
  caption_label: 'cal-dp-caption-label',
  month_grid: 'cal-dp-grid',
  weekdays: 'cal-dp-weekdays',
  weekday: 'cal-dp-weekday',
  week: 'cal-dp-week',
  day: 'cal-dp-day',
  day_button: 'cal-dp-day-button',
  today: 'cal-dp-today',
  selected: 'cal-dp-selected',
  outside: 'cal-dp-outside',
  disabled: 'cal-dp-disabled',
  hidden: 'cal-dp-hidden',
};

/** The picker's reach: 1900 to 2100, the Practice calendar's (and the Flutter app's) span. */
const PICKER_START_MONTH = new Date(1900, 0);
const PICKER_END_MONTH = new Date(2100, 11);

function PickerChevron({ orientation }: { orientation?: 'up' | 'down' | 'left' | 'right' }) {
  if (orientation === 'left') return <ChevronLeft aria-hidden className="cal-dp-chevron" />;
  if (orientation === 'right') return <ChevronRight aria-hidden className="cal-dp-chevron" />;
  return <ChevronDown aria-hidden className="cal-dp-chevron" />;
}

export interface CalendarPeriodControlsProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  calendarView: DateFilter;
  /** Appointments are refetching while the grid still shows data: a small spinner beside the steps. */
  refetching?: boolean;
}

export function CalendarPeriodControls({ selectedDate, onDateChange, calendarView, refetching = false }: CalendarPeriodControlsProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const unit = calendarView === 'Week' ? 'week' : calendarView === 'Month' ? 'month' : 'day';
  const prevLabel = `Previous ${unit}`;
  const nextLabel = `Next ${unit}`;
  return (
    <div className="cal-toolbar-period">
      <AnchoredPopover open={pickerOpen} onOpenChange={setPickerOpen}>
        <AnchoredPopoverTrigger
          render={<button type="button" aria-haspopup="dialog" aria-expanded={pickerOpen} className="cal-date-trigger" />}
        >
          {periodLabel(selectedDate, calendarView)}
          <ChevronDown aria-hidden className="cal-date-chevron" />
        </AnchoredPopoverTrigger>
        <AnchoredPopoverContent align="start" side="bottom" className="cal-date-popover">
          <DayPicker
            mode="single"
            captionLayout="dropdown"
            startMonth={PICKER_START_MONTH}
            endMonth={PICKER_END_MONTH}
            classNames={DAY_PICKER_CLASSNAMES}
            components={{ Chevron: PickerChevron }}
            formatters={{ formatMonthDropdown: (d) => d.toLocaleString('en-GB', { month: 'long' }) }}
            selected={selectedDate}
            defaultMonth={selectedDate}
            onSelect={(d) => {
              if (d) {
                onDateChange(d);
                setPickerOpen(false);
              }
            }}
            showOutsideDays
            fixedWeeks
          />
          <div className="cal-dp-footer">
            <button
              type="button"
              aria-label="Go to today"
              onClick={() => {
                onDateChange(practiceNow());
                setPickerOpen(false);
              }}
            >
              Today
            </button>
            <span>{periodLabel(selectedDate, 'Day')}</span>
          </div>
        </AnchoredPopoverContent>
      </AnchoredPopover>
      <div role="group" aria-label="Change period" className="cal-toolbar-steps">
        <button
          type="button"
          aria-label={prevLabel}
          className="cal-step"
          onClick={() => onDateChange(stepPeriod(selectedDate, calendarView, -1))}
        >
          <ChevronLeft aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Jump to today"
          disabled={showsToday(selectedDate, calendarView)}
          onClick={() => onDateChange(practiceNow())}
        >
          Today
        </button>
        <button
          type="button"
          aria-label={nextLabel}
          className="cal-step"
          onClick={() => onDateChange(stepPeriod(selectedDate, calendarView, 1))}
        >
          <ChevronRight aria-hidden />
        </button>
      </div>
      <span className="cal-refetch" data-slot="pa-calendar-refetch" data-active={refetching ? 'true' : 'false'}>
        {refetching ? <BrandSpinner size={16} label="Loading appointments" /> : null}
      </span>
    </div>
  );
}

export function CalendarViewSwitch({
  calendarView,
  onViewChange,
  views,
}: {
  calendarView: DateFilter;
  onViewChange: (view: DateFilter) => void;
  /** Limit the switch to these views (in VIEW_OPTIONS order). Absent → all five. */
  views?: readonly DateFilter[];
}) {
  const options = views ? VIEW_OPTIONS.filter((o) => views.includes(o.value)) : VIEW_OPTIONS;
  return (
    <Segmented<DateFilter> aria-label="Calendar view" options={options} value={calendarView} onChange={onViewChange} />
  );
}

export interface CalendarToolbarRowProps {
  /** The period (date picker, ‹ Today ›), first on the left. */
  period?: ReactNode;
  /** The practitioner filter (All ▾), centred in the room between the period and the trailing group. */
  center?: ReactNode;
  /** The appointment search, then the view switch, at the far right. Omitted: nothing is drawn. */
  search?: ReactNode;
  /** The view switch (Day / Week / Month / Room / Doctor), last on the right. */
  views?: ReactNode;
}

export function CalendarToolbarRow({ period, center, search, views }: CalendarToolbarRowProps) {
  return (
    // `data-calendar-date-row`: PM's hook the appointment search measures to fold under 1000 (PM `calendar-toolbar.tsx`).
    <div data-slot="pa-calendar-toolbar" data-calendar-date-row className="cal-toolbar">
      {period}
      <div className="cal-toolbar-center">{center}</div>
      <div className="cal-toolbar-trailing">
        {search}
        {views}
      </div>
    </div>
  );
}
